import { Controller } from "@hotwired/stimulus"
import marketBreadthChart from "../charts/highcharts/market_breadth_chart"
import gicsBreadthChart from "../charts/highcharts/gics_breadth_chart"
import indexPriceChart from "../charts/highcharts/index_price_chart"
import defaultChart from "../charts/highcharts/default_chart"
import { loadBundle } from "../utils/lazy_bundle"

export default class extends Controller {
  static targets = ["container", "config"]
  static values = { 
    chartData: Object,
    chartOptions: Object,
    chartType: String
  }

  async connect() {
    this.boundBeforeCache = this.beforeCache.bind(this)
    document.addEventListener("turbo:before-cache", this.boundBeforeCache)

    // Highcharts is no longer in the main bundle — fetch it first (a no-op
    // after the first chart on the first page load).
    try {
      await loadBundle("highcharts")
    } catch (error) {
      console.error(error)
    }

    // The user may have navigated away while the bundle was in flight, in
    // which case disconnect() has already run and there is nothing to draw.
    if (!this.element.isConnected) return

    this.initializeChart()
  }

  // Turbo caches the live DOM nodes, and this controller's disconnect() runs
  // AFTER the snapshot is taken but mutates those same nodes — chart.destroy()
  // was gutting the chart inside the cached snapshot, so browser-back restored
  // an empty container whose data-highchart-rendered flag made connect() treat
  // it as an intact cached chart (a silent blank box). Put the element back to
  // its server-rendered shape — config <script>, no chart, no flag — before
  // the snapshot is taken, so the restored page re-renders from scratch.
  beforeCache() {
    if (this.chart) {
      this.chart.destroy()
      this.chart = null
    }
    delete this.element.dataset.highchartRendered
    if (!this.hasConfigTarget && this.configJson) {
      const container = this.hasContainerTarget ? this.containerTarget : this.element
      container.innerHTML = ""
      const script = document.createElement("script")
      script.type = "application/json"
      script.setAttribute("data-highchart-target", "config")
      script.textContent = this.configJson
      this.element.appendChild(script)
    }
  }

  initializeChart() {
    // First check if Highcharts is available
    if (typeof Highcharts === 'undefined') {
      console.error("Highcharts is not available. Make sure it's properly loaded.")
      this.element.innerHTML = "<div class='alert alert-danger'>Highcharts library is not available. Please check the console for details.</div>"
      return
    }
    
    try {
      // Determine which chart type to create
      const chartType = this.chartTypeValue || 'default'
      
      // Get chart data from one of the available sources
      let chartData, chartOptions

      // 1. Try using config target (JSON in script tag). Keep the raw JSON:
      // rendering the chart replaces this <script>, and beforeCache needs it
      // to rebuild the server-rendered markup for Turbo's snapshot.
      if (this.hasConfigTarget) {
        this.configJson = this.configTarget.textContent
        const config = JSON.parse(this.configJson)
        chartData = config.data
        chartOptions = config.options
      }
      // 2. Try using the data values
      else if (this.hasChartDataValue) {
        chartData = this.chartDataValue
        chartOptions = this.hasChartOptionsValue ? this.chartOptionsValue : {}
        // Use data from values
      }
      // 3. Neither is available. If we've already rendered into this element
      // once, this is just a reconnect over a Turbo-cached snapshot (the render
      // replaced our <script data-highchart-target="config">), so there's nothing
      // to do — leave the cached chart in place. Only a genuinely unconfigured
      // element (never rendered) is a real error worth surfacing.
      else {
        if (this.element.dataset.highchartRendered) return

        console.error("No chart data or config found")
        this.element.innerHTML = "<div class='alert alert-danger'>Chart configuration not found. Please check the chart data.</div>"
        return
      }
      
      // Select the appropriate chart builder function
      let chartBuilder
      switch (chartType) {
        case 'market_breadth':
          chartBuilder = marketBreadthChart
          break
        case 'gics_breadth':
          chartBuilder = gicsBreadthChart
          break
        case 'index_price':
          chartBuilder = indexPriceChart
          break
        default:
          chartBuilder = defaultChart
      }
      
      // Use the container target if available, otherwise use the controller element
      const container = this.hasContainerTarget ? this.containerTarget : this.element
      
      // Create the chart
      const chart = chartBuilder(container, chartData, chartOptions)

      // Store chart reference for cleanup
      this.chart = chart

      // Mark the element as rendered so a later reconnect over a Turbo-cached
      // snapshot (where the config <script> is gone) is a silent no-op, not an
      // error that would replace the chart with a red box.
      this.element.dataset.highchartRendered = "true"
    } catch (error) {
      console.error(`Error initializing chart: ${error}`)
      const errorDiv = document.createElement('div')
      errorDiv.className = 'alert alert-danger'
      errorDiv.textContent = `Error creating chart: ${error.message}`
      this.element.innerHTML = ''
      this.element.appendChild(errorDiv)
    }
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this.boundBeforeCache)
    // Clean up the chart when the controller is disconnected
    if (this.chart) {
      this.chart.destroy()
    }
  }
}